/*
 * The Word Guess alphabet is German with real umlauts: A-Z plus Ä, Ö and Ü.
 *
 * There is deliberately no ß: JavaScript uppercases 'ß' to 'SS', which would change the
 * length of a word and break the fixed-width grid. Puzzle files must spell such words
 * with SS (GRUSS, FLUSS, SPASS), which is also the convention of the existing Krupion
 * puzzle data.
 */
export const WORD_GUESS_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÜ';

/*
 * Characters the physical keyboard may contribute. Everything else is filtered out by
 * PuzzleKeyboardService before it reaches the board.
 */
export const WORD_GUESS_KEY_FILTER_REGEX = /[^A-Za-zÄÖÜäöü]/g;

export const WORD_GUESS_DEFAULT_WORD_LENGTH = 5;
export const WORD_GUESS_DEFAULT_MAX_GUESSES = 6;

/*
 * Uppercases a single typed character and drops it if it is not part of the alphabet.
 * Returns an empty string for anything unusable.
 */
export function normalizeWordGuessLetter(rawLetter: string): string {
  if (!rawLetter) {
    return '';
  }

  const letter = rawLetter.toUpperCase();

  return WORD_GUESS_ALPHABET.indexOf(letter) === -1 ? '' : letter;
}

/*
 * Uppercases a whole word and strips every character outside the alphabet. Note that a
 * 'ß' expands to 'SS' through toUpperCase(), which is why puzzle authors must not use it.
 */
export function normalizeWordGuessWord(rawWord: string): string {
  if (!rawWord) {
    return '';
  }

  return rawWord
    .toUpperCase()
    .split('')
    .filter((letter) => WORD_GUESS_ALPHABET.indexOf(letter) !== -1)
    .join('');
}

/*
 * True when the word consists exclusively of alphabet letters and has the expected length.
 */
export function isWordGuessWord(rawWord: string, wordLength: number): boolean {
  const word = normalizeWordGuessWord(rawWord);

  return word.length === wordLength && word.length === (rawWord || '').length;
}
